import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import moment from 'moment'
import { ISO_DATE_FORMAT, ISO_DATE_SHORT_FORMAT } from 'constants/dateFormats'
import { toBaseFareClass, type FareClassWithReward } from 'constants/flight'
import { EmptyArray } from 'lib/array/arrayUtils'
import { useMemo } from 'react'
import useAllCharterFlightJourneys from './useAllCharterFlightJourneys'
import { CHARTER_BUNDLE_DURATION_OPTIONS } from 'constants/charterFlight'

interface DateRange {
  oneWayBundle?: boolean
  missingFlightLeg?: 'depart' | 'return'
  departureDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  durationDays: number // Total days from departureDate to returnDate
  nights: number // Nights from checkIn to checkOut
}

export default function useCharterDateRangesByMonth({
  charterBundleRoute,
  occupancies = EmptyArray,
  fareClass,
  disabled,
  feederOriginAirportCode,
}: {
  charterBundleRoute?: App.CharterBundleRoute
  occupancies: Array<App.Occupants>
  fareClass: FareClassWithReward
  disabled?: boolean
  /**
   * Forwarded to /flightsearch so this hook shares a Redux cache key with feeder-aware
   * callers (e.g. useBundleFlightData). Without this, two parallel /flightsearch requests
   * fire (one with, one without feederOriginAirportCode), and feeder-priced fares populate
   * only one of the cache entries.
   */
  feederOriginAirportCode?: string
}) {
  const allowIncompleteJourneys = useFeatureFlag(OptimizelyFeatureFlags.flightsIncompleteCharterBundles)
  const baseFareClass = toBaseFareClass(fareClass)
  const flightResults = useAllCharterFlightJourneys({
    charterBundleRoute,
    fareClass: baseFareClass,
    occupancies,
    disabled,
    feederOriginAirportCode,
  })

  // Extract dateRangesByMonth from all departing and returning flights
  const dateRangesByMonth = useMemo<Record<string, Array<DateRange>>>(() => {
    if (!flightResults?.fares?.[0] || !Array.isArray(flightResults.fares[0])) {
      return {}
    }

    const departingFares = flightResults.fares[0]
    const returningFares = flightResults.fares[1] || []

    // Create a map of returning fares by their ID for quick lookup
    const returningFaresById = new Map<string, App.JourneyV2>()
    returningFares.forEach(fare => {
      returningFaresById.set(fare.id, fare)
    })

    const dateRangesMap = new Map<string, DateRange>()
    const stayDates = new Set<string>()

    // Extract dates from flight pairs
    departingFares.forEach(departingFare => {
      const departingFlights = departingFare.flightGroup?.flights
      if (!departingFlights || departingFlights.length === 0) return

      const firstDepartingFlight = departingFlights[0]
      const lastDepartingFlight = departingFlights[departingFlights.length - 1]

      if (!firstDepartingFlight?.departingDate || !lastDepartingFlight?.arrivalDate) return

      const departureDate = moment(firstDepartingFlight.departingDate).format(ISO_DATE_FORMAT)
      const checkIn = moment(lastDepartingFlight.arrivalDate).format(ISO_DATE_FORMAT)

      // Find ALL corresponding returning flights using pairedFareId (may have multiple durations)
      if (departingFare.pairedFareId && departingFare.pairedFareId.length > 0) {
        // Process all paired return fares (e.g., 5 nights and 12 nights options)
        departingFare.pairedFareId.forEach(pairedId => {
          const returningFare = returningFaresById.get(pairedId)
          if (!returningFare) return

          const returningFlights = returningFare.flightGroup?.flights
          if (!returningFlights || returningFlights.length === 0) return

          const firstReturningFlight = returningFlights[0]
          const lastReturningFlight = returningFlights[returningFlights.length - 1]

          const returnDate = lastReturningFlight?.arrivalDate
            ? moment(lastReturningFlight.arrivalDate).format(ISO_DATE_FORMAT)
            : undefined
          const checkOut = firstReturningFlight?.departingDate
            ? moment(firstReturningFlight.departingDate).format(ISO_DATE_FORMAT)
            : undefined

          // Only add if we have all required dates
          if (departureDate && checkIn && returnDate && checkOut) {
            // Calculate durationDays (departureDate -> returnDate)
            const durationDays = moment(returnDate).diff(moment(departureDate), 'days')
            // Calculate nights (checkIn -> checkOut)
            const nights = moment(checkOut).diff(moment(checkIn), 'days')

            // Use departureDate + returnDate as key to support multiple durations for same departure date
            const uniqueKey = `${departureDate}-${returnDate}`
            if (!dateRangesMap.has(uniqueKey)) {
              stayDates.add(`${checkIn}-${checkOut}`)
              dateRangesMap.set(uniqueKey, {
                departureDate,
                returnDate,
                checkIn,
                checkOut,
                durationDays,
                nights,
              })
            }
          }
        })
      }
    })

    // A hotel stay can use either charter direction. The other direction is
    // selected separately at checkout; these dates are not invented charter fares.
    const addOneWayRange = (
      checkIn: string,
      checkOut: string,
      departureDate: string,
      returnDate = checkOut,
      missingFlightLeg: DateRange['missingFlightLeg'] = 'return',
    ) => {
      if (!allowIncompleteJourneys) return
      const stayKey = `${checkIn}-${checkOut}`
      if (stayDates.has(stayKey)) return
      stayDates.add(stayKey)
      const nights = moment(checkOut).diff(moment(checkIn), 'days')
      dateRangesMap.set(`${checkIn}-${checkOut}`, {
        checkIn,
        checkOut,
        departureDate,
        returnDate,
        durationDays: moment(returnDate).diff(moment(departureDate), 'days'),
        nights,
        oneWayBundle: true,
        missingFlightLeg,
      })
    }
    departingFares.forEach(fare => {
      const flights = fare.flightGroup.flights
      if (!flights[0]?.departingDate || !flights.at(-1)?.arrivalDate) return
      const checkIn = moment(flights.at(-1)?.arrivalDate).format(ISO_DATE_FORMAT)
      const departureDate = moment(flights[0]?.departingDate).format(ISO_DATE_FORMAT)
      CHARTER_BUNDLE_DURATION_OPTIONS.forEach(({ value }) => {
        addOneWayRange(checkIn, moment(checkIn).add(value, 'days').format(ISO_DATE_FORMAT), departureDate)
      })
    })
    returningFares.forEach(fare => {
      if (!fare.flightGroup.flights[0]?.departingDate || !fare.flightGroup.flights.at(-1)?.arrivalDate) return
      const returnDate = moment(fare.flightGroup.flights.at(-1)?.arrivalDate).format(ISO_DATE_FORMAT)
      const checkOut = moment(fare.flightGroup.flights[0]?.departingDate).format(ISO_DATE_FORMAT)
      CHARTER_BUNDLE_DURATION_OPTIONS.forEach(({ value }) => {
        const checkIn = moment(checkOut).subtract(value, 'days').format(ISO_DATE_FORMAT)
        addOneWayRange(checkIn, checkOut, checkIn, returnDate, 'depart')
      })
    })

    // Group by month while tracking earliest month start for sorting
    const groupedByMonth: Record<string, Array<DateRange>> = {}
    const monthSortKeyByMonth = new Map<string, number>()
    dateRangesMap.forEach((dateRange) => {
      const departureMoment = moment(dateRange.departureDate)
      const monthKey = departureMoment.format(ISO_DATE_SHORT_FORMAT)
      if (!groupedByMonth[monthKey]) {
        groupedByMonth[monthKey] = []
      }
      groupedByMonth[monthKey].push(dateRange)

      const monthStart = departureMoment.startOf('month').valueOf()
      const existingSortKey = monthSortKeyByMonth.get(monthKey)
      if (existingSortKey === undefined || monthStart < existingSortKey) {
        monthSortKeyByMonth.set(monthKey, monthStart)
      }
    })

    // Sort dates within each month
    Object.keys(groupedByMonth).forEach(monthKey => {
      groupedByMonth[monthKey].sort((a, b) => {
        // First sort by departureDate
        const departureDiff = moment(a.departureDate).valueOf() - moment(b.departureDate).valueOf()
        if (departureDiff !== 0) return departureDiff
        // If same departure date, sort by returnDate (shorter duration first)
        return moment(a.returnDate).valueOf() - moment(b.returnDate).valueOf()
      })
    })

    const orderedMonthKeys = Array.from(monthSortKeyByMonth.entries())
      .sort(([, a], [, b]) => a - b)
      .map(([monthKey]) => monthKey)

    const orderedByMonth: Record<string, Array<DateRange>> = {}
    orderedMonthKeys.forEach(monthKey => {
      orderedByMonth[monthKey] = groupedByMonth[monthKey]
    })

    return orderedByMonth
  }, [allowIncompleteJourneys, flightResults?.fares])

  return {
    dateRangesByMonth,
  }
}
